import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Save, Settings, CheckSquare, Filter } from 'lucide-react';
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';

const ENTITIES = [
  { value: 'AccountFinal', label: 'Accounts' },
  { value: 'PersonFinal', label: 'People' },
];

const ACCOUNT_OVERRIDE_FIELDS = [
  { key: 'account_fit_score', label: 'Account Fit Score', type: 'number' },
  { key: 'score_rationale', label: 'Score Rationale', type: 'textarea' },
  { key: 'decision_window_band', label: 'Decision Window Band', type: 'select', options: ['<12 months', '12-24 months', '>24 months', 'N/A'] },
  { key: 'decision_window_rationale', label: 'Decision Window Rationale', type: 'textarea' },
];

const PERSON_OVERRIDE_FIELDS = [
  { key: 'worked_for_customer', label: 'Worked for Customer', type: 'boolean' },
  { key: 'client_name', label: 'Client Name', type: 'text' },
  { key: 'archetype_name', label: 'Archetype Name', type: 'text' },
  { key: 'persona_fit_score', label: 'Persona Fit Score', type: 'number' },
  { key: 'persona_score_rationale', label: 'Persona Score Rationale', type: 'textarea' },
];

export default function TenantOverridesManager() {
  const [selectedEntity, setSelectedEntity] = useState('AccountFinal');
  const [selectedTenant, setSelectedTenant] = useState('smowl');
  const [searchTerm, setSearchTerm] = useState('');
  const [formerClientFilter, setFormerClientFilter] = useState('all');
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [overrideValues, setOverrideValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const loadRecords = async () => {
    setIsLoading(true);
    try {
      let data;
      if (selectedEntity === 'AccountFinal') {
        data = await AccountFinal.list();
      } else {
        data = await PersonFinal.list();
      }
      setRecords(data);
      setSelectedRecords(new Set());
      toast({ title: 'Records loaded', description: `Found ${data.length} records` });
    } catch (error) {
      console.error('Error loading records:', error);
      toast({ title: 'Error', description: 'Failed to load records', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectRecord = (record) => {
    setSelectedRecord(record);
    const existingOverrides = record.tenant_overrides?.[selectedTenant] || {};
    setOverrideValues(existingOverrides);
  };

  const toggleRecordSelection = (recordId) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
  };

  const handleBatchUpdate = async (field, value) => {
    if (selectedRecords.size === 0) {
      toast({ title: 'No records selected', variant: 'destructive' });
      return;
    }

    try {
      toast({ 
        title: 'Processing...', 
        description: `Updating ${selectedRecords.size} records. Please wait...`,
        duration: 10000
      });

      let successCount = 0;
      let errorCount = 0;

      for (const recordId of selectedRecords) {
        try {
          const record = records.find(r => r.id === recordId);
          const currentOverrides = record.tenant_overrides || {};
          const tenantOverrides = currentOverrides[selectedTenant] || {};
          
          const updatedOverrides = {
            ...currentOverrides,
            [selectedTenant]: {
              ...tenantOverrides,
              [field]: value
            }
          };

          if (selectedEntity === 'AccountFinal') {
            await AccountFinal.update(recordId, { tenant_overrides: updatedOverrides });
          } else {
            await PersonFinal.update(recordId, { tenant_overrides: updatedOverrides });
          }

          successCount++;
          
          if (successCount < selectedRecords.size) {
            await new Promise(resolve => setTimeout(resolve, 700));
          }

          if (successCount % 10 === 0) {
            toast({
              title: 'Progress...',
              description: `Updated ${successCount} of ${selectedRecords.size} records`,
              duration: 3000
            });
          }
        } catch (error) {
          console.error(`Error updating record ${recordId}:`, error);
          errorCount++;
        }
      }

      await loadRecords();
      setSelectedRecords(new Set());

      if (errorCount === 0) {
        toast({
          title: 'Success!',
          description: `Updated ${successCount} records for ${selectedTenant}`,
          duration: 5000
        });
      } else {
        toast({
          title: 'Partially completed',
          description: `${successCount} succeeded, ${errorCount} failed`,
          variant: 'default',
          duration: 7000
        });
      }
    } catch (error) {
      console.error('Batch update error:', error);
      toast({ title: 'Error', description: `Batch update failed: ${error.message}`, variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    if (!selectedRecord) return;

    try {
      const currentOverrides = selectedRecord.tenant_overrides || {};
      const updatedOverrides = {
        ...currentOverrides,
        [selectedTenant]: overrideValues
      };

      if (selectedEntity === 'AccountFinal') {
        await AccountFinal.update(selectedRecord.id, { tenant_overrides: updatedOverrides });
      } else {
        await PersonFinal.update(selectedRecord.id, { tenant_overrides: updatedOverrides });
      }

      setRecords(records.map(r => 
        r.id === selectedRecord.id 
          ? { ...r, tenant_overrides: updatedOverrides } 
          : r
      ));

      toast({ title: 'Saved', description: `Tenant overrides saved for ${selectedTenant}` });
    } catch (error) {
      console.error('Error saving:', error);
      toast({ title: 'Error', description: 'Failed to save overrides', variant: 'destructive' });
    }
  };

  const filteredRecords = records.filter(record => {
    // Text search filter
    const searchLower = searchTerm.toLowerCase();
    let matchesSearch = true;
    
    if (searchTerm) {
      if (selectedEntity === 'AccountFinal') {
        matchesSearch = record.account_name?.toLowerCase().includes(searchLower);
      } else {
        matchesSearch = `${record.first_name} ${record.last_name}`.toLowerCase().includes(searchLower) ||
               record.account_name?.toLowerCase().includes(searchLower) ||
               record.person_id?.toLowerCase().includes(searchLower);
      }
    }

    // Former Client filter (only for PersonFinal)
    let matchesFormerClientFilter = true;
    if (selectedEntity === 'PersonFinal' && formerClientFilter !== 'all') {
      const overrideValue = record.tenant_overrides?.[selectedTenant]?.worked_for_customer;
      const baseValue = record.worked_for_customer;
      const effectiveValue = overrideValue !== undefined ? overrideValue : baseValue;
      
      if (formerClientFilter === 'yes') {
        matchesFormerClientFilter = effectiveValue === true;
      } else if (formerClientFilter === 'no') {
        matchesFormerClientFilter = effectiveValue === false || effectiveValue === undefined;
      }
    }

    return matchesSearch && matchesFormerClientFilter;
  });

  const overrideFields = selectedEntity === 'AccountFinal' ? ACCOUNT_OVERRIDE_FIELDS : PERSON_OVERRIDE_FIELDS;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Tenant Field Overrides
        </CardTitle>
        <CardDescription>
          Set tenant-specific values for accounts and people. Smowl can see instructure/clairelabs data with custom fit scores and rationales.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label>Entity</Label>
            <Select value={selectedEntity} onValueChange={setSelectedEntity}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITIES.map(entity => (
                  <SelectItem key={entity.value} value={entity.value}>
                    {entity.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Target Tenant</Label>
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="smowl">Smowl</SelectItem>
                <SelectItem value="diambra">Diambra</SelectItem>
                <SelectItem value="instructure">Instructure</SelectItem>
                <SelectItem value="clairelabs">Claire Labs</SelectItem>
                <SelectItem value="demarque">Demarque</SelectItem>
                <SelectItem value="demarque_V2">Demarque V2</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedEntity === 'PersonFinal' && (
            <div className="space-y-2">
              <Label>Former Client Filter</Label>
              <Select value={formerClientFilter} onValueChange={setFormerClientFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes (Former Client)</SelectItem>
                  <SelectItem value="no">No (Not Former Client)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        <Button onClick={loadRecords} disabled={isLoading} className="w-full">
          {isLoading ? 'Loading...' : 'Load Records'}
        </Button>

        {/* Batch Actions for People */}
        {selectedEntity === 'PersonFinal' && selectedRecords.size > 0 && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-blue-900">
                {selectedRecords.size} record(s) selected
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedRecords(new Set())}
              >
                Clear Selection
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Quick Batch Actions for {selectedTenant}:</Label>
              <div className="flex gap-2 flex-wrap">
                <Button 
                  size="sm"
                  onClick={() => handleBatchUpdate('worked_for_customer', true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Set "Worked for Customer" = Yes
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => handleBatchUpdate('worked_for_customer', false)}
                >
                  Set "Worked for Customer" = No
                </Button>
              </div>
            </div>
          </div>
        )}

        {filteredRecords.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">
                  Select Record(s)
                  {formerClientFilter !== 'all' && (
                    <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      <Filter className="w-3 h-3 inline mr-1" />
                      Filtered: {filteredRecords.length}
                    </span>
                  )}
                </h3>
                {selectedEntity === 'PersonFinal' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (selectedRecords.size === filteredRecords.length) {
                        setSelectedRecords(new Set());
                      } else {
                        setSelectedRecords(new Set(filteredRecords.map(r => r.id)));
                      }
                    }}
                  >
                    <CheckSquare className="w-4 h-4 mr-1" />
                    {selectedRecords.size === filteredRecords.length ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
              </div>
              <div className="space-y-1">
                {filteredRecords.slice(0, 100).map(record => {
                  const overrideValue = record.tenant_overrides?.[selectedTenant]?.worked_for_customer;
                  const hasOverride = record.tenant_overrides?.[selectedTenant];
                  
                  return (
                    <div key={record.id} className="flex items-center gap-2">
                      {selectedEntity === 'PersonFinal' && (
                        <Checkbox
                          checked={selectedRecords.has(record.id)}
                          onCheckedChange={() => toggleRecordSelection(record.id)}
                        />
                      )}
                      <button
                        onClick={() => handleSelectRecord(record)}
                        className={`flex-1 text-left px-3 py-2 rounded text-sm hover:bg-gray-100 ${
                          selectedRecord?.id === record.id ? 'bg-blue-100 hover:bg-blue-100' : ''
                        }`}
                      >
                        {selectedEntity === 'AccountFinal' ? record.account_name : `${record.first_name} ${record.last_name}`}
                        {hasOverride && (
                          <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1 rounded">
                            Has Overrides
                          </span>
                        )}
                        {selectedEntity === 'PersonFinal' && overrideValue !== undefined && (
                          <span className={`ml-2 text-xs px-1 rounded ${overrideValue ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            Former Client: {overrideValue ? 'Yes' : 'No'}
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border rounded-lg p-4">
              {selectedRecord ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold">
                      {selectedEntity === 'AccountFinal' ? selectedRecord.account_name : `${selectedRecord.first_name} ${selectedRecord.last_name}`}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Primary Tenant: {selectedRecord.tenant_id || 'default'}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">Override Values for {selectedTenant}</h4>
                    {overrideFields.map(field => (
                      <div key={field.key} className="space-y-1">
                        <Label className="text-xs">{field.label}</Label>
                        {field.type === 'number' && (
                          <Input
                            type="number"
                            value={overrideValues[field.key] || ''}
                            onChange={(e) => setOverrideValues({
                              ...overrideValues,
                              [field.key]: parseFloat(e.target.value)
                            })}
                            placeholder={`Default: ${selectedRecord[field.key] || 'N/A'}`}
                          />
                        )}
                        {field.type === 'text' && (
                          <Input
                            type="text"
                            value={overrideValues[field.key] || ''}
                            onChange={(e) => setOverrideValues({
                              ...overrideValues,
                              [field.key]: e.target.value
                            })}
                            placeholder={`Default: ${selectedRecord[field.key] || 'N/A'}`}
                          />
                        )}
                        {field.type === 'textarea' && (
                          <Textarea
                            value={overrideValues[field.key] || ''}
                            onChange={(e) => setOverrideValues({
                              ...overrideValues,
                              [field.key]: e.target.value
                            })}
                            placeholder={`Default: ${selectedRecord[field.key] || 'N/A'}`}
                            rows={3}
                          />
                        )}
                        {field.type === 'select' && (
                          <Select
                            value={overrideValues[field.key] || selectedRecord[field.key] || ''}
                            onValueChange={(value) => setOverrideValues({
                              ...overrideValues,
                              [field.key]: value
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select value" />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options.map(opt => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {field.type === 'boolean' && (
                          <Select
                            value={String(overrideValues[field.key] ?? selectedRecord[field.key] ?? false)}
                            onValueChange={(value) => setOverrideValues({
                              ...overrideValues,
                              [field.key]: value === 'true'
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="true">Yes</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    ))}
                  </div>

                  <Button onClick={handleSave} className="w-full">
                    <Save className="w-4 h-4 mr-2" />
                    Save Overrides for {selectedTenant}
                  </Button>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  Select a record to configure overrides
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}